import { CSSProperties } from 'react';
import { imgPath } from '../../../common/utilities/ImgUrl';
import { SessionService } from '../../../commonInfra/services/SessionService';
import { IResult } from '../../../httpClient/serverApi/types/IResponse';
import WebHttpClient from '../../../httpClient/WebHttpClient';
import { getSessionManagementServiceInstance } from '../../../sessionManagement/SessionManagementService';
import { ISize } from '../../../ts/models/ISize';
import { ServerSettingsUnauthenticated } from '../../services/ServerSettingsUnauthenticated';
import { StartPageService } from '../../services/StartPageService';
import { ICustomLogo } from '../../types/IServerSettingsUnauthenticated';

export type LogoType = 'header' | 'compact' | 'signin';

export interface ILogo {
  image: string;
  width: number;
  height: number;
  href: string;
  title: string;
  styleOverrides?: CSSProperties;
}

export namespace LogoService {

  // TODO: Should imgPath incorporate the static asset prefix?
  const DefaultSignInLogoImage = StaticAssetsUrlPrefix + imgPath('tableau_sign_in_logo.svg');
  const DefaultSignInLogoWidth = 277;
  const DefaultSignInLogoHeight = 58;

  // default logo
  const DefaultNavPanelLogo = imgPath('Tableau_Logo_Mark.svg');
  const DefaultNavPanelLogoWidth = 40;
  const DefaultNavPanelLogoHeight = 40;

  // compact logo
  const DefaultCompactLogo = imgPath('Tableau_Logo_Mark.svg');
  const DefaultCompactLogoWidth = 24;
  const DefaultCompactLogoHeight = 24;

  // These maximums are known by tabadmin
  const MaximumHeaderLogoWidth = 160;
  const MaximumHeaderLogoHeight = 160;
  const MaximumSignInLogoWidth = 3000;
  const MaximumSignInLogoHeight = 3000;
  const MaximumCompactLogoWidth = 32;
  const MaximumCompactLogoHeight = 32;

  /**
   * Call the updateSiteLogo web client REST API to update a site logo.
   * @param file      the file to set as the new logo, or falsey to reset the logo
   * @param logoType  which logo to update
   */
  export function updateSiteLogo(file: File, logoType: LogoType): ng.IPromise<IResult> {
    const sessionCookieName = SessionService.getSessionCookieName();
    const sessionId = SessionService.getSessionId();

    const formData = new FormData();

    if (file) {
      formData.append('updateSiteCustomLogoInput', file);
    }
    formData.append(sessionCookieName, sessionId);
    formData.append('useCustomLogo', file ? 'true' : 'false');
    formData.append('useIframeResponse', 'false');
    formData.append('logoType', logoType);

    return WebHttpClient.requestPostFormToRestApi(formData, 'updateSiteLogo');
  }

  export function displaySiteLogoCustomization(): boolean {
    const sessionManagementService = getSessionManagementServiceInstance();
    return (sessionManagementService.isSessionServerAdmin() || (sessionManagementService.isSessionSiteAdmin() && sessionManagementService.isOnlineEnvironment()))
      && sessionManagementService.isSiteCustomizationEnabled();
  }

  export function isUsingCustomSiteLogo(): boolean {
    const sessionManagementService = getSessionManagementServiceInstance();
    return sessionManagementService.isSiteCustomizationEnabled() && !!(sessionManagementService.siteLogo());
  }

  export function isUsingCustomServerLogo(): boolean {
    const serverCustomization = ServerSettingsUnauthenticated.serverCustomization();
    return !!(serverCustomization && serverCustomization.customHeaderLogo);
  }

  export function isUsingCustomCompactSiteLogo(): boolean {
    const sessionManagementService = getSessionManagementServiceInstance();

    return sessionManagementService.isSiteCustomizationEnabled() && !!(sessionManagementService.compactLogo());

  }

  export function isUsingCustomCompactServerLogo(): boolean {
    const serverCustomization = ServerSettingsUnauthenticated.serverCustomization();
    return !!(serverCustomization && serverCustomization.customCompactLogo);
  }

  export function getNavPanelLogo(): ILogo {
    if (isUsingCustomSiteLogo()) {
      const sessionManagementService = getSessionManagementServiceInstance();

      return getCustomLogo(
        sessionManagementService.siteLogo(),
        MaximumHeaderLogoWidth,
        MaximumHeaderLogoHeight,
        sessionManagementService.sessionSiteName());
    } else {
      return getServerHeaderLogo();
    }
  }

  export function getCompactLogo(): ILogo {
    if (isUsingCustomCompactSiteLogo() || isUsingCustomSiteLogo()) {
      const sessionManagementService = getSessionManagementServiceInstance();

      return getCustomLogo(
        sessionManagementService.compactLogo() || sessionManagementService.siteLogo(),
        MaximumCompactLogoWidth,
        MaximumCompactLogoHeight,
        sessionManagementService.sessionSiteName());
    } else {
      return getServerCompactLogo();
    }
  }

  export function hasCustomCompactLogo(): boolean {
    return isUsingCustomCompactSiteLogo() || isUsingCustomCompactServerLogo();
  }

  export function hasCustomHeaderLogo(): boolean {
    return isUsingCustomSiteLogo() || isUsingCustomServerLogo();
  }

  function getServerHeaderLogo(): ILogo {
    const serverCustomization = ServerSettingsUnauthenticated.serverCustomization();
    if (serverCustomization && serverCustomization.customHeaderLogo) {
      return getCustomLogo(
        serverCustomization.customHeaderLogo,
        MaximumHeaderLogoWidth,
        MaximumHeaderLogoHeight,
        ServerSettingsUnauthenticated.serverName());
    } else {
      return getDefaultHeaderLogo();
    }
  }

  export function getDefaultHeaderLogo(): ILogo {
    return {
      image: DefaultNavPanelLogo,
      width: DefaultNavPanelLogoWidth,
      height: DefaultNavPanelLogoHeight,
      href: getStartPageHref(),
      title: ServerSettingsUnauthenticated.serverName()
    };
  }

  function getServerCompactLogo(): ILogo {
    const serverCustomization = ServerSettingsUnauthenticated.serverCustomization();
    if (serverCustomization && serverCustomization.customCompactLogo) {
      return getCustomLogo(
        serverCustomization.customCompactLogo,
        MaximumCompactLogoWidth,
        MaximumCompactLogoHeight,
        ServerSettingsUnauthenticated.serverName());

    } else if (serverCustomization && serverCustomization.customHeaderLogo) {
      // No custom compact logo but have a custom header logo-- use it, but resize it
      return getCustomLogo(
        serverCustomization.customHeaderLogo,
        MaximumCompactLogoWidth,
        MaximumCompactLogoHeight,
        ServerSettingsUnauthenticated.serverName());

    } else {
      // no custom compact or header logo
      return getDefaultCompactLogo();
    }
  }

  export function getDefaultCompactLogo(): ILogo {
    return {
      image: DefaultCompactLogo,
      width: DefaultCompactLogoWidth,
      height: DefaultCompactLogoHeight,
      href: getStartPageHref(),
      title: ServerSettingsUnauthenticated.serverName()
    };
  }

  export function getSignInLogo(): ILogo {
    const serverCustomization = ServerSettingsUnauthenticated.serverCustomization();
    if (serverCustomization && serverCustomization.customSignInLogo) {
      return getCustomLogo(
        serverCustomization.customSignInLogo,
        MaximumSignInLogoWidth,
        MaximumSignInLogoHeight,
        ServerSettingsUnauthenticated.serverName());
    } else {
      return {
        image: DefaultSignInLogoImage,
        width: DefaultSignInLogoWidth,
        height: DefaultSignInLogoHeight,
        href: '', // No need for sign in
        title: ServerSettingsUnauthenticated.serverName()
      };
    }
  }

  function getStartPageHref(): string {
    const currentUserStartPage: string = StartPageService.getCurrentUserStartPageForCurrentSite();
    return '#' + currentUserStartPage;
  }

  export function getCustomLogo(logo: ICustomLogo, maxWidth: number, maxHeight: number, title: string): ILogo {
    const scaledSize = LogoService.getCustomLogoNavPanelAdjustedSize(logo, maxWidth, maxHeight);
    return {
      ...scaledSize,
      image: logo.path,
      href: logo.link || getStartPageHref(),
      title: title
    };
  }

  export function getScaledLogo(logo: ILogo, logoType: LogoType): ILogo {
    const maxWidth = getMaxWidthForLogoType(logoType);
    const maxHeight = getMaxHeightForLogoType(logoType);
    return {
      ...logo,
      ...LogoService.getCustomLogoNavPanelAdjustedSize(logo, maxWidth, maxHeight)
    };
  }

  export function getMaxWidthForLogoType(logoType: LogoType): number {
    switch (logoType) {
      case 'header':
        return MaximumHeaderLogoWidth;
      case 'compact':
        return MaximumCompactLogoWidth;
      case 'signin':
        return MaximumSignInLogoWidth;
    }
    return 0;
  }

  export function getMaxHeightForLogoType(logoType: LogoType): number {
    switch (logoType) {
      case 'header':
        return MaximumHeaderLogoHeight;
      case 'compact':
        return MaximumCompactLogoHeight;
      case 'signin':
        return MaximumSignInLogoHeight;
    }
    return 0;
  }

  export function getCustomLogoNavPanelAdjustedSize(logoDimensions: ISize, maxWidth: number, maxHeight: number): ISize {
    const width = logoDimensions.width;
    const height = logoDimensions.height;
    const ratio = Math.min(maxWidth / width, maxHeight / height, 1);
    return { width: width * ratio, height: height * ratio };
  }
}

export type LogoService = typeof LogoService;
